import { FP_DEFAULTS, FreePlayState, StartVoteAttempt, VoteChoice, VoteContext, VoteResult } from './types';

export const initialFreePlayState = (): FreePlayState => ({
    phase: 'idle',
    currentGameID: null,
    vote: null,
    initiatorCooldowns: {},
    lastVoteOutcome: null,
});

const tally = (vote: VoteContext): { yes: number; no: number; pending: number } => {
    const values = Object.values(vote.votes);
    const yes = values.filter((v) => v === 'yes').length;
    const no = values.filter((v) => v === 'no').length;
    const pending = Math.max(0, vote.eligibleVoters.length - yes - no);
    return { yes, no, pending };
};

const majorityThreshold = (quorum: number) => Math.floor(quorum / 2) + 1;

const evaluate = (vote: VoteContext, now: number): VoteResult => {
    const { yes, no, pending } = tally(vote);
    const threshold = majorityThreshold(vote.eligibleVoters.length);
    if (yes >= threshold) return 'accepted';
    if (no >= threshold) return 'rejected';
    /* All voters have cast a vote — settle immediately rather than waiting for the timer.
       Reached when threshold can't be met (ties on even quorum, e.g. 2-2 on 4). */
    if (pending === 0) return yes > no ? 'accepted' : 'rejected';
    if (now >= vote.deadline) return yes > no ? 'accepted' : 'expired';
    return 'pending';
};

export const VoteService = {
    initial: initialFreePlayState,

    startGame(state: FreePlayState, gameID: string): FreePlayState {
        return {
            ...state,
            phase: 'playing',
            currentGameID: gameID,
            vote: null,
            // Drop any prior vote result so it can't leak into the next game.
            lastVoteOutcome: null,
        };
    },

    endGame(state: FreePlayState): FreePlayState {
        return {
            ...state,
            phase: 'idle',
            currentGameID: null,
            vote: null,
            lastVoteOutcome: null,
        };
    },

    canInitiate(state: FreePlayState, deviceID: string, now: number): boolean {
        const until = state.initiatorCooldowns[deviceID];
        return !until || now >= until;
    },

    startVote(
        state: FreePlayState,
        params: {
            initiatorID: string;
            proposedGameID: string;
            activePlayers: string[];
            now: number;
            durationMs?: number;
        }
    ): StartVoteAttempt {
        const { initiatorID, proposedGameID, activePlayers, now } = params;
        const durationMs = params.durationMs ?? FP_DEFAULTS.voteDurationMs;

        if (state.phase !== 'playing') return { ok: false, reason: 'wrong_phase' };
        if (state.currentGameID === proposedGameID) return { ok: false, reason: 'same_game' };
        if (!VoteService.canInitiate(state, initiatorID, now)) return { ok: false, reason: 'cooldown' };
        if (activePlayers.length === 0) return { ok: false, reason: 'no_active_players' };

        /* Lone player: a vote among a single person is meaningless — switch directly,
           no overlay (decision 2026-06-18). The host passes a single-element voter pool
           when only one player is connected/active. */
        if (activePlayers.length <= 1) {
            return {
                ok: true,
                state: {
                    ...state,
                    phase: 'playing',
                    currentGameID: proposedGameID,
                    vote: null,
                    lastVoteOutcome: null,
                },
            };
        }

        const initiatorIsActive = activePlayers.includes(initiatorID);
        const vote: VoteContext = {
            initiatorID,
            proposedGameID,
            eligibleVoters: [...activePlayers],
            startedAt: now,
            deadline: now + durationMs,
            votes: initiatorIsActive ? { [initiatorID]: 'yes' } : {},
        };

        return {
            ok: true,
            state: { ...state, phase: 'voting', vote },
        };
    },

    castVote(state: FreePlayState, deviceID: string, choice: VoteChoice): FreePlayState {
        if (state.phase !== 'voting' || !state.vote) return state;
        if (!state.vote.eligibleVoters.includes(deviceID)) return state;
        if (state.vote.votes[deviceID] === choice) return state;

        return {
            ...state,
            vote: {
                ...state.vote,
                votes: { ...state.vote.votes, [deviceID]: choice },
            },
        };
    },

    /**
     * Drop voters who are no longer connected from an in-flight vote, along with the
     * votes they had cast. Keeps the quorum honest: a player who walks away mid-vote
     * shouldn't count as a phantom "pending" voice that forces everyone to wait out the
     * full deadline (decision 2026-06-18). Returns the SAME reference when nothing
     * changed, so callers can skip a redundant broadcast.
     */
    reconcileVoters(state: FreePlayState, connectedIDs: string[]): FreePlayState {
        if (state.phase !== 'voting' || !state.vote) return state;
        const connected = new Set(connectedIDs);
        const eligibleVoters = state.vote.eligibleVoters.filter((id) => connected.has(id));
        if (eligibleVoters.length === state.vote.eligibleVoters.length) return state;
        const votes: Record<string, VoteChoice> = {};
        Object.entries(state.vote.votes).forEach(([id, choice]) => {
            if (connected.has(id)) votes[id] = choice;
        });
        return { ...state, vote: { ...state.vote, eligibleVoters, votes } };
    },

    /**
     * Everyone who could vote left mid-vote → drop the proposal and resume the running
     * game. No cooldown: nobody was around to reject anything (decision 2026-06-18).
     */
    abandonVote(state: FreePlayState): FreePlayState {
        if (state.phase !== 'voting') return state;
        return { ...state, phase: 'playing', vote: null };
    },

    tally,

    evaluate,

    settle(
        state: FreePlayState,
        now: number,
        cooldownMs: number = FP_DEFAULTS.initiatorCooldownMs
    ): { state: FreePlayState; result: VoteResult } {
        if (state.phase !== 'voting' || !state.vote) {
            return { state, result: 'pending' };
        }

        const result = evaluate(state.vote, now);
        if (result === 'pending') return { state, result };

        const { initiatorID, proposedGameID } = state.vote;
        /* Recorded for everyone (TV result banner + a toast on every gamepad). `result`
           is never 'pending' here. */
        const outcome = { result, initiatorID, proposedGameID, at: now };

        if (result === 'accepted') {
            return {
                state: {
                    ...state,
                    phase: 'playing',
                    currentGameID: proposedGameID,
                    vote: null,
                    lastVoteOutcome: outcome,
                },
                result,
            };
        }

        return {
            state: {
                ...state,
                phase: 'playing',
                vote: null,
                initiatorCooldowns: {
                    ...state.initiatorCooldowns,
                    [initiatorID]: now + cooldownMs,
                },
                lastVoteOutcome: outcome,
            },
            result,
        };
    },
};
